import {
  AlertState,
  type TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatDecimal } from '$shared/utils/charts.utils';
import { getLocalizedTime } from '$shared/utils/date.utils';

import { CustomTooltipV2 } from '$features/monitoring/history-charts-v2/custom-tooltip-v2';
import { useHistoryChartsConfig } from '$features/monitoring/history-charts-v2/useHistoryChartsConfig';
import { getIconNameForParameterName } from '$features/monitoring/monitoring.utils';
import { createColorClassFor } from '$features/monitoring/parameters/utils';

import { useDashboardDeviceHistory } from '../dashboard.api';

interface ParameterMiniChartProps {
  manufacturer: string;
  serialNumber: string;
  telemetryId: string;
  parameter?: TelemetryHistoryEnrichedDTO;
  alertState?: string;
}

export function ParameterMiniChart({
  manufacturer,
  serialNumber,
  telemetryId,
  parameter,
  alertState,
}: ParameterMiniChartProps) {
  const { t } = useTranslation();
  const { showThresholdRefs, chartColors } = useHistoryChartsConfig();
  const historyQuery = useDashboardDeviceHistory({
    manufacturer,
    serialNumber,
    telemetryId,
  });
  const parameterName = parameter?.name || telemetryId;
  const fractionalDigits = parameter?.fractionalDigits ?? 1;
  const latestValue =
    typeof parameter?.valueDouble === 'number' && Number.isFinite(parameter.valueDouble)
      ? formatDecimal(parameter.valueDouble, fractionalDigits)
      : '-';
  const historyData = historyQuery.data?.historyData ?? [];
  const hasProcessValues = historyData.some((point) => Number.isFinite(point.avg));
  const firstTimestamp = historyData.at(0)?.timestamp;
  const lastTimestamp = historyData.at(-1)?.timestamp;
  const timeTicks = [firstTimestamp, lastTimestamp].filter(
    (timestamp, index, timestamps): timestamp is number =>
      typeof timestamp === 'number' && timestamps.indexOf(timestamp) === index,
  );
  const unit = historyQuery.data?.unit || '';
  const valueColorClass =
    alertState === AlertState.Idle ||
    alertState === AlertState.Warn ||
    alertState === AlertState.Alert
      ? createColorClassFor(alertState, 'color')
      : undefined;

  return (
    <section className="parameter-mini-chart">
      <div className="parameter-mini-chart__header">
        <div className="parameter-mini-chart__headline">
          <Icon
            name={getIconNameForParameterName(parameterName)}
            size={IconSizeClasses.XSmall}
            className="ic_parameters"
          />
          <div>
            <h5>{parameterName}</h5>
          </div>
        </div>
        <div className="parameter-mini-chart__latest">
          <strong className={cn(valueColorClass)}>
            {latestValue}
            {unit ? ` ${unit}` : ''}
          </strong>
          {parameter?.sentAt && <span>{getLocalizedTime(parameter.sentAt)}</span>}
        </div>
      </div>

      {historyQuery.isLoading && (
        <div className="parameter-mini-chart__feedback">
          <DotsLoader />
        </div>
      )}
      {historyQuery.isError && (
        <div className="parameter-mini-chart__feedback">
          <span>{t('dashboard.deviceOverview.chartLoadError')}</span>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={() => {
              historyQuery.refetch();
            }}
          >
            {t('dashboard.deviceOverview.retry')}
          </Button>
        </div>
      )}
      {historyQuery.data && !hasProcessValues && (
        <div className="parameter-mini-chart__feedback">
          {t('dashboard.deviceOverview.noHistory')}
        </div>
      )}
      {historyQuery.data && hasProcessValues && (
        <div
          className="parameter-mini-chart__chart"
          role="img"
          aria-label={t('dashboard.deviceOverview.chartAria', { parameterName })}
        >
          <ResponsiveContainer width="100%" height={136}>
            <LineChart
              data={historyData}
              margin={{ top: 8, right: 6, bottom: 0, left: -10 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="timestamp"
                domain={['dataMin', 'dataMax']}
                ticks={timeTicks}
                tickFormatter={getLocalizedTime}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                minTickGap={24}
              />
              <YAxis
                domain={['auto', 'auto']}
                tickFormatter={(value: number) => formatDecimal(value, fractionalDigits)}
                axisLine={false}
                tickLine={false}
                tickCount={3}
                width={52}
              />
              <Tooltip
                content={
                  <CustomTooltipV2 unit={unit} fractionalDigits={fractionalDigits} />
                }
              />
              {showThresholdRefs && (
                <>
                  <Line
                    type="linear"
                    dataKey="warnHigh"
                    name={t('dashboard.deviceOverview.warningHigh')}
                    stroke={chartColors.setPointColor}
                    dot={false}
                    connectNulls={false}
                  />
                  <Line
                    type="linear"
                    dataKey="warnLow"
                    name={t('dashboard.deviceOverview.warningLow')}
                    stroke={chartColors.setPointColor}
                    dot={false}
                    connectNulls={false}
                  />
                  <Line
                    type="linear"
                    dataKey="alertHigh"
                    name={t('dashboard.deviceOverview.alertHigh')}
                    stroke={chartColors.alertHighLowColor}
                    dot={false}
                    connectNulls={false}
                  />
                  <Line
                    type="linear"
                    dataKey="alertLow"
                    name={t('dashboard.deviceOverview.alertLow')}
                    stroke={chartColors.alertHighLowColor}
                    dot={false}
                    connectNulls={false}
                  />
                </>
              )}
              <Line
                type="linear"
                dataKey="avg"
                name={parameterName}
                stroke={chartColors.processValueColor}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
